import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Copy, KeyRound, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { useApi, BACKEND_URL } from "@/lib/api";

export default function ShortcutPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [copied, setCopied] = useState("");
  const { data: status, refetch } = useQuery({
    queryKey: ["capture-token"],
    queryFn: () => api.get("/capture-token"),
  });

  const captureUrl = `${BACKEND_URL}/api/shortcut/capture`;
  const bodyJson = JSON.stringify({ url: "Link from the input step", note: "" }, null, 2);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success("Copied");
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Copy failed — long-press to select manually");
    }
  };

  const generate = async () => {
    try {
      const r = await api.post("/capture-token");
      setToken(r.token);
      refetch();
      toast.success("Token created — copy it now, it's shown only once");
    } catch {
      toast.error("Could not create token");
    }
  };

  const revoke = async () => {
    try {
      await api.del("/capture-token");
      setToken(null);
      refetch();
      toast.success("Token revoked");
    } catch {
      toast.error("Could not revoke token");
    }
  };

  const active = token || (status?.configured ? null : null);

  return (
    <div data-testid="shortcut-page" className="mx-auto max-w-xl space-y-6 animate-fade-in">
      <button
        data-testid="shortcut-back-button"
        onClick={() => navigate("/")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl glass-card">
          <Zap className="h-5 w-5 text-primary" />
        </span>
        <div>
          <h1 className="font-serif text-3xl tracking-tight">Apple Shortcut</h1>
          <p className="text-xs text-muted-foreground">Save any link to your Brain in one tap, straight from iOS.</p>
        </div>
      </div>

      <div className="rounded-3xl glass-card p-6 space-y-4">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">
          <KeyRound className="h-3.5 w-3.5" /> Capture token
        </p>
        {token ? (
          <div>
            <code data-testid="token-display" className="block break-all rounded-2xl bg-stone-900/90 text-emerald-300 font-mono text-xs px-4 py-3.5">
              {token}
            </code>
            <div className="mt-3 flex gap-2">
              <button
                data-testid="copy-token-button"
                onClick={() => copy(token, "token")}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white px-4 py-2.5 text-xs font-semibold active:scale-95 transition-transform"
              >
                {copied === "token" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                Copy token
              </button>
              <button
                data-testid="revoke-token-button"
                onClick={revoke}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 text-xs font-semibold text-destructive active:scale-95 transition-transform"
              >
                <Trash2 className="h-3.5 w-3.5" /> Revoke
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-stone-500">
              {status?.configured
                ? "A token is already active on this account. Generating a new one replaces it."
                : "No token yet. Create one — it's shown once, then stored only as an unreadable hash."}
            </p>
            <button
              data-testid="generate-token-button"
              onClick={generate}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-semibold shadow-md shadow-primary/20 active:scale-95 transition-transform"
            >
              <KeyRound className="h-4 w-4" /> {status?.configured ? "New token" : "Create token"}
            </button>
          </div>
        )}
      </div>

      <div className="rounded-3xl glass-card p-6 space-y-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">Build it (once, ~1 minute)</p>
        <ol className="space-y-4 text-sm text-stone-600">
          <li className="flex gap-3">
            <Num n="1" />
            <span className="flex-1 min-w-0">
              Open <strong>Shortcuts</strong> → <strong>+</strong>. Add <strong>Ask for Input</strong> (Input Type: Text,
              Prompt: “Link”). Add <strong>Get Contents of URL</strong> and paste this URL:
              <button
                data-testid="copy-url-button"
                onClick={() => copy(captureUrl, "url")}
                className="mt-2 flex w-full items-center gap-2 rounded-xl bg-white/70 border border-white/60 px-3 py-2 text-left"
              >
                <code className="flex-1 truncate font-mono text-[11px] text-stone-600">{captureUrl}</code>
                {copied === "url" ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5 text-stone-400" />}
              </button>
            </span>
          </li>
          <li className="flex gap-3">
            <Num n="2" />
            <span className="flex-1 min-w-0">
              In that action set <strong>Method: POST</strong> → Header <strong>Authorization</strong> ={" "}
              <code className="font-mono text-[11px]">Bearer your-token</code> → Request Body{" "}
              <strong>JSON</strong>:
              <button
                data-testid="copy-body-button"
                onClick={() => copy(bodyJson, "body")}
                className="mt-2 flex w-full items-center gap-2 rounded-xl bg-white/70 border border-white/60 px-3 py-2 text-left"
              >
                <code className="flex-1 truncate font-mono text-[11px] text-stone-600">{bodyJson.replace(/\n\s*/g, " ")}</code>
                {copied === "body" ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5 text-stone-400" />}
              </button>
            </span>
          </li>
          <li className="flex gap-3">
            <Num n="3" />
            <span className="flex-1 min-w-0">
              Name it <strong>Save to Becoming</strong>, tap the share sheet toggle so you can share any link from
              Safari or YouTube straight into your archive. AI writes the summary and a logo — you just tap save.
            </span>
          </li>
        </ol>
      </div>
    </div>
  );
}

function Num({ n }) {
  return (
    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
      {n}
    </span>
  );
}
